import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { api } from '../api/client';
import type { V2Turn, HistoryTurn } from '../api/types';
import { diag } from '../lib/diag';

/**
 * OLDER transcript history to PREPEND above the live `/v2` view (design #1204,
 * revised per Alex 2026-06-23: the live `/v2` render stays the primary, correct
 * source — history is only appended ABOVE it when scrolling up).
 *
 * The hard part is the SEAM: `/v2` turns have no uuid/timestamp, so we locate
 * where the live window begins inside `/history` by matching the user-message
 * of the oldest live turn(s). Everything in `/history` OLDER than that match is
 * what we show above the live view.
 *
 * The very top live turn is often cut off by the scrape window → we try the
 * first few live turns as anchors; when we match on live turn #k, history's
 * complete versions of live turns 0..k-1 are included in `older` and the caller
 * drops the first k (partial) live turns (`liveDropCount`) so the seam neither
 * duplicates nor gaps.
 *
 * If `/history` is empty or the anchor isn't found, `older` stays empty — the
 * live view renders exactly as today (graceful no-op).
 */

const ANCHOR_TRIES = 4; // how many leading live turns to try as the seam anchor

// The seam matches an anchor user-message across two sources that format it
// DIFFERENTLY: `/v2` strips the `[IACP:msgid:sender]` envelope off inbound IACP
// pushes, while `/history` (raw .jsonl) keeps it. Without stripping it here too,
// federation-collab agents — whose turns are mostly IACP pushes — never match
// their anchor, so `older` stays empty and the scroll-back affordance never
// appears (Alex 2026-06-25: BL-Hospitality showed only the live list). `(System)`
// is the `/v2` placeholder for system-origin turns and is never a usable anchor.
const IACP_ENVELOPE = /^\[IACP:[^\]]*\]\s*/;
function norm(s: string | undefined | null): string {
  const t = (s || '').replace(IACP_ENVELOPE, '').replace(/\s+/g, ' ').trim();
  if (t === '(System)') return '';
  // CLI slash-commands (`/resume`, `/model opus`, ...) never appear as user
  // turns in the transcript .jsonl — verified 0 hits over Knowledge's 90MB
  // (2026-07-16). As anchors they can NEVER match, which made a freshly
  // resumed pane (System banner + /resume turns) kill the whole scroll-back:
  // "anchor NOT found -> older=[]" -> Alex sah einen leeren Chat. Treat them
  // like (System): not an anchor. With ALL anchors empty the empty-pane
  // fallback renders the newest history page instead.
  if (/^\/[a-zA-Z][\w:-]*(\s|$)/.test(t)) return '';
  return t.slice(0, 120);
}

export interface OlderHistory {
  older: HistoryTurn[];
  /** Live `/v2` turns enriched with exact transcript timing when matched. */
  liveWithTiming: V2Turn[];
  hasMore: boolean;
  loadingOlder: boolean;
  loadOlder: () => void;
  /** Re-read the newest transcript page, e.g. when a live turn settles. */
  refreshNewest: () => void;
  /** How many leading live turns history already covers → caller slices them. */
  liveDropCount: number;
}

/**
 * Join durable transcript timing onto the live pane turns without replacing
 * their richer `/v2` content. The first usable live message is anchored to the
 * newest matching history occurrence (same seam rule as scroll-back), then the
 * remaining turns are matched forwards in order. This keeps repeated prompts
 * and IACP envelopes deterministic instead of using an unordered text map.
 */
export function mergeHistoryTiming(liveTurns: V2Turn[], historyTurns: HistoryTurn[]): V2Turn[] {
  if (!liveTurns.length || !historyTurns.length) return liveTurns;

  let liveAnchor = -1;
  let historyAnchor = -1;
  for (let li = 0; li < liveTurns.length && historyAnchor < 0; li++) {
    const key = norm(liveTurns[li].user);
    if (!key) continue;
    for (let hi = historyTurns.length - 1; hi >= 0; hi--) {
      if (norm(historyTurns[hi].user) === key) {
        liveAnchor = li;
        historyAnchor = hi;
        break;
      }
    }
  }
  if (historyAnchor < 0) return liveTurns;

  let nextHistory = historyAnchor;
  return liveTurns.map((turn, li) => {
    if (li < liveAnchor) return turn;
    const key = norm(turn.user);
    if (!key) return turn;
    let match = -1;
    for (let hi = nextHistory; hi < historyTurns.length; hi++) {
      if (norm(historyTurns[hi].user) === key) {
        match = hi;
        break;
      }
    }
    if (match < 0) return turn;
    nextHistory = match + 1;
    const historical = historyTurns[match];
    return {
      ...turn,
      timestamp: historical.timestamp,
      ended_at: historical.ended_at,
      duration_s: historical.duration_s,
      submitted_by: historical.submitted_by,
    };
  });
}

export function useOlderHistory(
  sessionName: string | null,
  liveTurns: V2Turn[],
  /** true once /v2 has SETTLED (data !== null) - distinguishes "empty pane"
   *  from "still loading"; needed for the empty-pane fallback below. */
  liveLoaded: boolean = false,
): OlderHistory {
  const [older, setOlder] = useState<HistoryTurn[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [liveDropCount, setLiveDropCount] = useState(0);
  const [newestHistory, setNewestHistory] = useState<HistoryTurn[]>([]);
  // Retry ticker: a transient /history failure (flaky network) bumps this to re-run
  // the seam resolve a few times, so scroll-back self-heals instead of staying
  // silently broken until a new turn arrives (Alex, flaky-net, #204/#206 follow-up).
  const [retryTick, setRetryTick] = useState(0);
  const retryRef = useRef(0);

  const seamKeyRef = useRef<string | null>(null);   // anchor-set we resolved for
  const seenUuids = useRef<Set<string>>(new Set());
  const activeRef = useRef(sessionName);
  activeRef.current = sessionName;
  // Parallel prefetch (Alex 2026-07-03, seam perf): /history needs the live ANCHORS only
  // to MATCH, not to FETCH — but the fetch used to wait for /v2 to land first, so remote
  // agents saw scroll-back only after ~0.5s (/v2) + ~3s (/history) SERIAL. Kick the fetch
  // off in parallel with /v2 on session switch; the anchor-resolve awaits it instead of
  // fetching. Used once, then later anchor shifts refetch fresh as before.
  const prefetchRef = useRef<{ session: string; promise: Promise<Awaited<ReturnType<typeof api.sessions.history>> | null> } | null>(null);
  useEffect(() => {
    if (!sessionName) { prefetchRef.current = null; return; }
    prefetchRef.current = {
      session: sessionName,
      promise: api.sessions.history(sessionName).catch(() => null), // fail → normal fetch path
    };
  }, [sessionName]);

  // Synchronous reset on session change.
  const [stored, setStored] = useState(sessionName);
  if (stored !== sessionName) {
    setStored(sessionName);
    setOlder([]);
    setCursor(null);
    setHasMore(false);
    setLoadingOlder(false);
    setLiveDropCount(0);
    setNewestHistory([]);
    seamKeyRef.current = null;
    seenUuids.current = new Set();
    retryRef.current = 0;
  }

  // Normalised user-messages of the first few live turns = the seam anchors.
  const anchors = liveTurns.slice(0, ANCHOR_TRIES).map((t) => norm(t.user));
  // Empty-pane fallback (Cloud/Alex 2026-07-16, Knowledge nach mac-Reboot):
  // a restarted pane has NO usable live turns (none at all, or only System
  // banners + slash-commands like /resume that never exist in the transcript)
  // -> no anchors -> the seam resolve never ran and a 90MB transcript rendered
  // as a BLANK view. Nothing in such a pane can duplicate history, so show the
  // newest history page wholesale. Checked over ALL live turns (not just the
  // first ANCHOR_TRIES) so a later matchable turn keeps the normal seam path.
  // Sentinel key -> the first REAL live turn re-resolves via anchorKey change.
  const emptyPane = liveLoaded && !liveTurns.some((t) => norm(t.user));
  const anchorKey = anchors.some(Boolean) ? anchors.join('') : (emptyPane ? '<empty-pane>' : '');

  // Resolve the seam against the newest history page when the anchors appear
  // (or shift, e.g. the scrape window slides as the conversation grows).
  useEffect(() => {
    if (!sessionName || !anchorKey) return;
    if (seamKeyRef.current === anchorKey) return;
    let cancelled = false;
    (async () => {
      try {
        // Use the parallel prefetch when it's for this session (consume once);
        // otherwise — later anchor shifts, retries — fetch fresh.
        const pf = prefetchRef.current;
        prefetchRef.current = null;
        const prefetched = pf && pf.session === sessionName ? await pf.promise : null;
        const res = prefetched ?? await api.sessions.history(sessionName);
        if (cancelled || activeRef.current !== sessionName) return;
        const turns = res.turns || [];
        setNewestHistory(turns);
        if (!turns.length) {
          diag('history-seam: /history EMPTY → no scroll-back', { agent: sessionName });
          seamKeyRef.current = anchorKey; return;
        }
        // Empty pane -> no seam to find: the whole newest page IS the view.
        if (anchorKey === '<empty-pane>') {
          seamKeyRef.current = anchorKey;
          seenUuids.current = new Set(turns.map((t) => t.uuid));
          setOlder(turns);
          setCursor(res.next_cursor);
          setHasMore(!!res.has_more);
          setLiveDropCount(0);
          retryRef.current = 0;
          diag('history-seam: EMPTY pane -> newest page wholesale', { agent: sessionName, older: turns.length, hasMore: !!res.has_more });
          return;
        }
        // Find the first live anchor (0..k) that exists in the newest page;
        // match newest→oldest so we hit the live window's copy.
        let seamIdx = -1;
        let dropK = 0;
        for (let k = 0; k < anchors.length; k++) {
          const a = anchors[k];
          if (!a) continue;
          for (let i = turns.length - 1; i >= 0; i--) {
            if (norm(turns[i].user) === a) { seamIdx = i; dropK = k; break; }
          }
          if (seamIdx >= 0) break;
        }
        seamKeyRef.current = anchorKey;
        if (seamIdx < 0) {
          // Anchor not in the newest page — don't risk a duplicate seam.
          diag('history-seam: anchor NOT found in newest page → no scroll-back', {
            agent: sessionName, historyTurns: turns.length, anchors: anchors.filter(Boolean).length,
          });
          setOlder([]);
          setHasMore(false);
          setLiveDropCount(0);
          return;
        }
        const olderTurns = turns.slice(0, seamIdx);
        seenUuids.current = new Set(olderTurns.map((t) => t.uuid));
        setOlder(olderTurns);
        setCursor(res.next_cursor);
        setHasMore(!!res.has_more);
        setLiveDropCount(dropK);
        retryRef.current = 0;
        diag('history-seam: resolved', { agent: sessionName, older: olderTurns.length, dropK, hasMore: !!res.has_more });
      } catch (e) {
        // Transient /history failure (flaky network) — the likely cause of Alex's
        // "scroll-back ended quickly" (#204/#206). DON'T mark the anchor resolved, and
        // self-heal: retry a few times on a short delay instead of staying silently
        // broken. (A stable new turn also re-triggers via anchorKey.)
        diag('history-seam: /history fetch FAILED → retry', { agent: sessionName, attempt: retryRef.current + 1, err: e instanceof Error ? e.message : String(e) });
        if (!cancelled && retryRef.current < 4) {
          retryRef.current += 1;
          setTimeout(() => { if (activeRef.current === sessionName) setRetryTick((t) => t + 1); }, 3000);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionName, anchorKey, retryTick]);

  const loadOlder = useCallback(() => {
    if (!sessionName || !cursor || !hasMore || loadingOlder) return;
    setLoadingOlder(true);
    (async () => {
      try {
        const res = await api.sessions.history(sessionName, cursor);
        if (activeRef.current !== sessionName) return;
        const fresh = (res.turns || []).filter((t) => !seenUuids.current.has(t.uuid));
        fresh.forEach((t) => seenUuids.current.add(t.uuid));
        if (fresh.length) setOlder((prev) => [...fresh, ...prev]);
        setCursor(res.next_cursor);
        setHasMore(!!res.has_more);
      } catch { /* keep what we have */ }
      finally { if (activeRef.current === sessionName) setLoadingOlder(false); }
    })();
  }, [sessionName, cursor, hasMore, loadingOlder]);

  const refreshNewest = useCallback(() => {
    if (!sessionName) return;
    api.sessions.history(sessionName)
      .then((res) => {
        if (activeRef.current === sessionName) setNewestHistory(res.turns || []);
      })
      .catch(() => { /* timing enhancement degrades to the live view */ });
  }, [sessionName]);

  const liveWithTiming = useMemo(
    () => mergeHistoryTiming(liveTurns, newestHistory),
    [liveTurns, newestHistory],
  );

  return { older, liveWithTiming, hasMore, loadingOlder, loadOlder, refreshNewest, liveDropCount };
}
